import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useParams } from '@ui/app-router';
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuItem } from '@ui/primitives/sidebar';
import { Skeleton } from '@ui/primitives/skeleton';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useGetProjectSidebarSearch, useSideBarProjectList } from 'ze-sdk';
import { ProjectItem } from './project-item';
export function ProjectsList(param) {
    let { organization, expandedProjects, onToggleProject, onExpandProject, onExpandAll, onCollapseAll, searchQuery = '' } = param;
    var _ref, _ref1;
    const params = useParams({
        from: '/org/$organization/$project',
        shouldThrow: false
    });
    const paramProject = (params === null || params === void 0 ? void 0 : params.project) || '';
    const { data: projectListResponse, isLoading: projectsLoading } = useSideBarProjectList({
        organization
    });
    const projectList = (_ref = projectListResponse === null || projectListResponse === void 0 ? void 0 : projectListResponse.entities) !== null && _ref !== void 0 ? _ref : [];
    const getProjectLabel = (project)=>{
        var _project_displayName;
        return (_project_displayName = project.displayName) !== null && _project_displayName !== void 0 ? _project_displayName : project.name;
    };
    const hasAutoExpandedRef = useRef(false);
    const isSearchActive = searchQuery.trim().length > 0;
    // Single request for all projects + matching apps when search is active.
    // Replaces N parallel useGetProjectApplicationSidebarList calls.
    const { data: searchResponse, isLoading: searchLoading } = useGetProjectSidebarSearch({
        organization
    }, {
        q: searchQuery.trim()
    }, {
        query: {
            enabled: isSearchActive
        }
    });
    const searchResults = (_ref1 = searchResponse === null || searchResponse === void 0 ? void 0 : searchResponse.entities) !== null && _ref1 !== void 0 ? _ref1 : [];
    // Build a lookup: projectId → matching applications from the search response
    const searchAppsByProjectId = new Map(searchResults.map((p)=>[
            p.id,
            p.applications
        ]));
    useEffect(()=>{
        if (paramProject && projectList && onExpandProject && !hasAutoExpandedRef.current) {
            const currentProject = projectList.find((p)=>p.name === paramProject);
            if (currentProject && !expandedProjects.has(currentProject.id)) {
                onExpandProject(currentProject.id);
                hasAutoExpandedRef.current = true;
            }
        }
    }, [
        paramProject,
        projectList,
        onExpandProject,
        expandedProjects
    ]);
    if (projectsLoading) {
        return /*#__PURE__*/ _jsxs(SidebarGroup, {
            "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_GROUP",
            children: [
                /*#__PURE__*/ _jsx(SidebarGroupLabel, {
                    className: "text-xs font-medium text-muted-foreground/70 uppercase tracking-wider",
                    "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_LABEL",
                    children: "Loading..."
                }),
                /*#__PURE__*/ _jsx(SidebarGroupContent, {
                    children: /*#__PURE__*/ _jsxs(SidebarMenu, {
                        "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_MENU",
                        children: [
                            /*#__PURE__*/ _jsx(SidebarMenuItem, {
                                children: /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-6 w-full"
                                })
                            }),
                            /*#__PURE__*/ _jsx(SidebarMenuItem, {
                                children: /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-6 w-full"
                                })
                            }),
                            /*#__PURE__*/ _jsx(SidebarMenuItem, {
                                children: /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-6 w-full"
                                })
                            })
                        ]
                    })
                })
            ]
        });
    }
    if (!projectList || projectList.length === 0) {
        return /*#__PURE__*/ _jsxs(SidebarGroup, {
            "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_GROUP",
            children: [
                /*#__PURE__*/ _jsx(SidebarGroupLabel, {
                    className: "text-xs font-medium text-muted-foreground/70 uppercase tracking-wider",
                    "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_LABEL",
                    children: "Projects"
                }),
                /*#__PURE__*/ _jsx(SidebarGroupContent, {
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "text-sm text-muted-foreground p-2",
                        "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_EMPTY",
                        children: "No projects found"
                    })
                })
            ]
        });
    }
    const allProjectsExpanded = projectList.every((project)=>expandedProjects.has(project.id));
    const someProjectsExpanded = expandedProjects.size > 0;
    const handleToggleAll = ()=>{
        if (allProjectsExpanded || someProjectsExpanded) {
            onCollapseAll === null || onCollapseAll === void 0 ? void 0 : onCollapseAll();
        } else {
            const allProjectIds = projectList.map((project)=>project.id);
            onExpandAll === null || onExpandAll === void 0 ? void 0 : onExpandAll(allProjectIds);
        }
    };
    // When searching, only show projects returned by the search endpoint.
    // The search endpoint already filters to projects that match or have matching apps.
    const visibleProjects = isSearchActive ? projectList.filter((p)=>searchAppsByProjectId.has(p.id)) : projectList;
    return /*#__PURE__*/ _jsxs(SidebarGroup, {
        "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_GROUP",
        children: [
            /*#__PURE__*/ _jsxs(SidebarGroupLabel, {
                className: "text-xs font-medium text-muted-foreground/70 uppercase tracking-wider flex items-center justify-between group",
                "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_LABEL",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-mono font-medium text-xs uppercase tracking-normal text-muted-foreground",
                        children: "Projects"
                    }),
                    !isSearchActive && projectList.length > 0 && /*#__PURE__*/ _jsx("button", {
                        onClick: handleToggleAll,
                        className: "opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-sidebar-accent rounded-sm",
                        title: allProjectsExpanded ? 'Collapse all projects' : 'Expand all projects',
                        "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_TOGGLE_ALL",
                        children: allProjectsExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                            className: "h-3 w-3"
                        }) : /*#__PURE__*/ _jsx(ChevronRight, {
                            className: "h-3 w-3"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SidebarGroupContent, {
                className: "text-gray-25",
                children: /*#__PURE__*/ _jsx(SidebarMenu, {
                    "data-e2e": "LAYOUT_SIDEBAR__PROJECTS_MENU",
                    children: isSearchActive && searchLoading ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SidebarMenuItem, {
                                children: /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-6 w-full"
                                })
                            }),
                            /*#__PURE__*/ _jsx(SidebarMenuItem, {
                                children: /*#__PURE__*/ _jsx(Skeleton, {
                                    className: "h-6 w-full"
                                })
                            })
                        ]
                    }) : visibleProjects.sort((a, b)=>getProjectLabel(a).localeCompare(getProjectLabel(b))).map((project)=>{
                        var _searchAppsByProjectId_get;
                        return /*#__PURE__*/ _jsx(ProjectItem, {
                            project: {
                                id: project.id,
                                name: project.name,
                                displayName: getProjectLabel(project)
                            },
                            organization: organization,
                            isExpanded: expandedProjects.has(project.id),
                            onToggle: ()=>{
                                onToggleProject(project.id);
                            },
                            searchQuery: searchQuery,
                            searchApplications: isSearchActive ? (_searchAppsByProjectId_get = searchAppsByProjectId.get(project.id)) !== null && _searchAppsByProjectId_get !== void 0 ? _searchAppsByProjectId_get : [] : undefined
                        }, project.id);
                    })
                })
            })
        ]
    });
}
